import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import DepartmentDashboard from './pages/DepartmentDashboard';
import ReportDetail from './pages/ReportDetail';
import DocumentList from './pages/DocumentList';
import DocumentDetail from './pages/DocumentDetail';
import DocumentCreate from './pages/DocumentCreate';
import PlanList from './pages/PlanList';
import PlanDetail from './pages/PlanDetail';
import PlanCreate from './pages/PlanCreate';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import UserList from './pages/UserList';
import UserCreate from './pages/UserCreate';
import AuditLogList from './pages/AuditLogList';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import AIAssistant from './pages/AIAssistant';
import PresentationList from './pages/PresentationList';
import PresentationCreate from './pages/PresentationCreate';
import ReportConstructor from './pages/ReportConstructor';
import ReportComparison from './pages/ReportComparison';
import DirectoryManager from './pages/DirectoryManager';
import SystemSettings from './pages/Settings';
import WeeklyControl from './pages/WeeklyControl';
import InformationConstructor from './pages/InformationConstructor';
import SearchResults from './pages/SearchResults';
import HRDashboard from './pages/HRDashboard';
import EmployeeKPI from './pages/EmployeeKPI';
import TasksDashboard from './pages/TasksDashboard';
import TeamsManager from './pages/TeamsManager';
import ProjectList from './pages/ProjectList';
import ProjectBoard from './pages/ProjectBoard';
import MyTasks from './pages/MyTasks';
import AutomationsList from './pages/AutomationsList';
import ResourceLoad from './pages/ResourceLoad';
import { isLeadership, hasFullReadAccess, isAdminOnly } from './utils/permissions';
import './App.css';

// Protected Route — проверяет токен и роль (RBAC)
function ProtectedRoute({ children, requireAdmin = false, requireFullRead = false, requireLeadership = false }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // requireAdmin — только admin (настройки, пользователи, справочники)
  if (requireAdmin && !isAdminOnly(user)) {
    return <Navigate to="/dashboard/department" replace />;
  }

  // requireFullRead — руководство + аналитик (аналитика, AI, экспорт, конструкторы)
  if (requireFullRead && !hasFullReadAccess(user)) {
    return <Navigate to="/dashboard/department" replace />;
  }

  // requireLeadership — руководство (создание/редактирование документов)
  if (requireLeadership && !isLeadership(user)) {
    return <Navigate to="/dashboard/department" replace />;
  }

  return children;
}

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Все защищённые маршруты внутри Layout */}
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute requireLeadership={true}>
                <Layout><AdminDashboard /></Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/department"
            element={
              <ProtectedRoute>
                <Layout><DepartmentDashboard /></Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/:id"
            element={
              <ProtectedRoute>
                <Layout><ReportDetail /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Документооборот */}
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Layout><DocumentList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/create"
            element={
              <ProtectedRoute requireLeadership={true}>
                <Layout><DocumentCreate /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/:id"
            element={
              <ProtectedRoute>
                <Layout><DocumentDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/:id/edit"
            element={
              <ProtectedRoute requireLeadership={true}>
                <Layout><DocumentCreate /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Аналитика */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><AnalyticsDashboard /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Конструктор отчётов */}
          <Route
            path="/reports/constructor"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><ReportConstructor /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Сравнительный анализ */}
          <Route
            path="/analytics/compare"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><ReportComparison /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Планирование */}
          <Route
            path="/plans"
            element={
              <ProtectedRoute>
                <Layout><PlanList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/plans/create"
            element={
              <ProtectedRoute>
                <Layout><PlanCreate /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/plans/:id"
            element={
              <ProtectedRoute>
                <Layout><PlanDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/plans/:id/edit"
            element={
              <ProtectedRoute>
                <Layout><PlanCreate /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Пользователи (admin) */}
          <Route
            path="/users"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout><UserList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/create"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout><UserCreate /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id/edit"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout><UserCreate /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Журнал аудита (admin) */}
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout><AuditLogList /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Справочники (admin) */}
          <Route
            path="/directories"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout><DirectoryManager /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Настройки системы (admin) */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout><SystemSettings /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Профиль */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout><Profile /></Layout>
              </ProtectedRoute>
            }
          />

          {/* ИИ-помощник */}
          <Route
            path="/ai-assistant"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><AIAssistant /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Презентации */}
          <Route
            path="/presentations"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><PresentationList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/presentations/create"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><PresentationCreate /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/presentations/:id"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><PresentationCreate /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Контроль недели (Приоритет 13) */}
          <Route
            path="/weekly-control"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><WeeklyControl /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Отдел кадров (HR) */}
          <Route
            path="/hr"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><HRDashboard /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Конструктор информаций (Приоритет 14) */}
          <Route
            path="/information-constructor"
            element={
              <ProtectedRoute requireFullRead={true}>
                <Layout><InformationConstructor /></Layout>
              </ProtectedRoute>
            }
          />

          {/* KPI сотрудников */}
          <Route
            path="/kpi"
            element={
              <ProtectedRoute>
                <Layout><EmployeeKPI /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Проекты и задачи */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Layout><TasksDashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/teams"
            element={
              <ProtectedRoute>
                <Layout><TeamsManager /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/projects"
            element={
              <ProtectedRoute>
                <Layout><ProjectList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/projects/:id"
            element={
              <ProtectedRoute>
                <Layout><ProjectBoard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/my"
            element={
              <ProtectedRoute>
                <Layout><MyTasks /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/automations"
            element={
              <ProtectedRoute>
                <Layout><AutomationsList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/resource-load"
            element={
              <ProtectedRoute>
                <Layout><ResourceLoad /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Уведомления */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Layout><Notifications /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Глобальный поиск */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <Layout><SearchResults /></Layout>
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
